import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ChevronRight, Star, Folder, MapPin } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Modal,
  Field,
  LoadingState,
  ErrorState,
  EmptyState,
  inputClass,
} from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import type { Closet } from '@/lib/types';

export function ClosetsPage() {
  const { data, loading, error, refetch } = useApi<{ closets: Closet[] }>('/closets');
  const { mutate, loading: saving } = useMutation();

  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [isMain, setIsMain] = useState(false);

  const closets = data?.closets ?? [];
  const totalItems = closets.reduce((sum, c) => sum + (c.itemCount ?? 0), 0);

  const resetForm = () => {
    setName('');
    setLocation('');
    setIsMain(false);
  };

  const handleAdd = async () => {
    if (!name.trim()) return;
    const res = await mutate('post', '/closets', {
      name: name.trim(),
      locationText: location.trim() || undefined,
      isMain,
    });
    if (res !== null) {
      setShowAdd(false);
      resetForm();
      refetch();
    }
  };

  return (
    <Page>
      <PageHeader section="Closets" backHref="/profile" backLabel="Profile" />

      <Main className="space-y-5">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && (
          <>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-foreground">{closets.length}</p>
                  <p className="text-sm text-muted-foreground">Total Closets</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-foreground">{totalItems}</p>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                </div>
              </div>
            </Card>

            <Button fullWidth onClick={() => setShowAdd(true)}>
              <Plus className="w-4 h-4" />
              Add Closet
            </Button>

            {closets.length === 0 ? (
              <EmptyState
                icon={<Folder className="w-8 h-8" />}
                title="No closets yet"
                description="Add a closet to organize where your items live."
              />
            ) : (
              <Card className="overflow-hidden divide-y divide-border">
                {closets.map((closet) => (
                  <Link
                    key={closet.id}
                    to={`/profile/closets/${closet.id}`}
                    className="flex items-center gap-4 p-4 hover:bg-secondary/50 transition-colors"
                  >
                    <div className={`p-3 rounded-xl ${closet.isMain ? 'bg-primary/10' : 'bg-secondary'}`}>
                      {closet.isMain ? (
                        <Star className="w-5 h-5 text-primary" />
                      ) : (
                        <Folder className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{closet.name}</p>
                        {closet.isMain && (
                          <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-[10px] font-medium">
                            Main
                          </span>
                        )}
                      </div>
                      {closet.locationText && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">{closet.locationText}</p>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-foreground">{closet.itemCount ?? 0}</p>
                      <p className="text-xs text-muted-foreground">items</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </Link>
                ))}
              </Card>
            )}
          </>
        )}
      </Main>

      <Modal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        title="Add New Closet"
        footer={
          <Button fullWidth onClick={handleAdd} disabled={!name.trim() || saving}>
            {saving ? 'Adding…' : 'Add Closet'}
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Closet Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., His Closet, Coat Closet"
              className={inputClass}
            />
          </Field>
          <Field label="Physical Location">
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Master Bedroom, Newark NJ"
              className={inputClass}
            />
          </Field>
          <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
            <div>
              <p className="text-sm font-medium text-foreground">Set as Main Closet</p>
              <p className="text-xs text-muted-foreground mt-0.5">Your default "In Closet" location</p>
            </div>
            <button
              type="button"
              onClick={() => setIsMain((v) => !v)}
              className={`relative w-12 h-7 rounded-full transition-colors ${isMain ? 'bg-primary' : 'bg-border'}`}
            >
              <div
                className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${isMain ? 'left-6' : 'left-1'}`}
              />
            </button>
          </div>
        </div>
      </Modal>
    </Page>
  );
}
